<template>
    <div class="register-container">
        <p class="title">{{ titleText }}</p>
        <form class="registration-form">
            <div class="name-input-wrapper">
                <div class="first-name-input-wrapper">
                    <label for="first-name-input">Name *</label>
                    <input
                        v-model="$v.userData.register.first_name.$model"
                        v-autofocus
                        @keydown="$v.userData.register.first_name.$touch"
                        class="first-name-input"
                        name="first-name-input"
                        type="text"
                        placeholder="First name"
                    />
                    <span
                        class="error-form-message"
                        v-if="
                            $v.userData.register.first_name.$dirty &&
                            !$v.userData.register.first_name.required
                        "
                    >
                        First name is required.
                    </span>
                    <span
                        class="error-form-message"
                        v-else-if="
                            $v.userData.register.first_name.$dirty &&
                            !$v.userData.register.first_name.minLength
                        "
                    >
                        Please enter more than 2 characters.
                    </span>
                    <span
                        class="error-form-message"
                        v-else-if="
                            $v.userData.register.first_name.$dirty &&
                            !$v.userData.register.first_name.maxLength
                        "
                    >
                        Please enter less than 25 characters.
                    </span>
                </div>
                <div class="last-name-input-wrapper">
                    <input
                        v-model="$v.userData.register.last_name.$model"
                        @keydown="$v.userData.register.last_name.$touch"
                        class="last-name-input"
                        name="last-name-input"
                        type="text"
                        placeholder="Last name"
                    />
                    <span
                        class="error-form-message"
                        v-if="
                            $v.userData.register.last_name.$dirty &&
                            !$v.userData.register.last_name.required
                        "
                    >
                        Last name is required.
                    </span>
                    <span
                        class="error-form-message"
                        v-else-if="
                            $v.userData.register.last_name.$dirty &&
                            !$v.userData.register.last_name.minLength
                        "
                    >
                        Please enter more than 2 characters.
                    </span>
                    <span
                        class="error-form-message"
                        v-else-if="
                            $v.userData.register.last_name.$dirty &&
                            !$v.userData.register.last_name.maxLength
                        "
                    >
                        Please enter less than 25 characters.
                    </span>
                </div>
            </div>
            <div class="password-input-wrapper">
                <label for="password-input">Password *</label>
                <div class="password-input-with-icon">
                    <input
                        v-model="$v.userData.register.password.$model"
                        :type="visible ? 'text' : 'password'"
                        @keydown="$v.userData.register.password.$touch"
                        class="password-input"
                        name="password-input"
                        placeholder="8+ characters, include both letters and numbers"
                    />
                    <i
                        class="fa"
                        :class="visible ? 'fa-eye' : 'fa-eye-slash'"
                        aria-hidden="true"
                        @click="visible = !visible"
                    ></i>
                </div>
                <span
                    class="error-form-message"
                    v-if="
                        $v.userData.register.password.$dirty &&
                        !$v.userData.register.password.required
                    "
                >
                    Password is required.
                </span>
                <span
                    class="error-form-message"
                    v-if="
                        $v.userData.register.password.$dirty &&
                        !$v.userData.register.password.minLength
                    "
                >
                    Please enter at least 8 characters.
                </span>
                <span
                    class="error-form-message"
                    v-if="
                        $v.userData.register.password.$dirty &&
                        !$v.userData.register.password.maxLength
                    "
                >
                    Please enter less than 25 characters.
                </span>
                <span
                    class="error-form-message"
                    v-if="
                        $v.userData.register.password.$dirty &&
                        !$v.userData.register.password.containsLetter
                    "
                >
                    Please use at least one letter in your password.
                </span>
                <span
                    class="error-form-message"
                    v-if="
                        $v.userData.register.password.$dirty &&
                        !$v.userData.register.password.containsNumber
                    "
                >
                    Please use at least one number in your password.
                </span>
            </div>
            <div class="terms-msg-wrapper">
                <span class="terms-msg"> By clicking "Continue", I agree to Hedlyner's </span>
                <a href="/terms-of-service">Terms of Service</a>
                <span class="terms-msg">and</span>
                <a href="/privacy-policy">Privacy Policy</a>.
            </div>
        </form>
    </div>
</template>
<script>
    import { required, minLength, maxLength } from 'vuelidate/lib/validators'

    export default {
        name: 'Register',
        props: {
            userData: {
                type: Object,
                required: true
            },
            validation: {
                type: Object,
                required: false
            },
            titleText: {
                type: String,
                required: false,
                default: 'Tell us a bit more'
            }
        },
        data: () => ({
            visible: false
        }),
        watch: {
            $v: {
                handler: function (validation) {
                    this.$emit('update:validation', validation)
                },
                immediate: true
            }
        },
        validations: {
            userData: {
                register: {
                    first_name: {
                        required,
                        minLength: minLength(2),
                        maxLength: maxLength(25)
                    },
                    last_name: { required, minLength: minLength(2), maxLength: maxLength(25) },
                    password: {
                        required,
                        minLength: minLength(8),
                        maxLength: maxLength(25),
                        containsNumber: function (value) {
                            return /[0-9]/.test(value)
                        },
                        containsLetter: function (value) {
                            return /[a-zA-Z]/.test(value)
                        }
                    }
                }
            }
        }
    }
</script>
<style lang="scss" scoped>
    @import '../../../../sass/_variables.scss';
    .register-container {
        p {
            margin-bottom: 0px;
        }
        button {
            border: none;
        }

        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        width: 100%;
        gap: 40px;

        .title {
            color: #dedede;
            font-family: Instrument Sans;
            font-size: 38px;
            font-style: normal;
            font-weight: 500;
            line-height: 110%;
            letter-spacing: -0.76px;
        }
        .registration-form {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 16px;
            align-self: stretch;

            .error-form-message {
                color: #ed3c0d;
                font-family: Instrument Sans;
                font-size: 8px;
                font-style: normal;
                font-weight: 600;
                line-height: 130%;
                letter-spacing: 0.14px;
            }
            label {
                padding: 4px 2px;
                color: #acacac;
                font-family: Instrument Sans;
                font-size: 12px;
                font-style: normal;
                font-weight: 650;
                line-height: 135%;
                letter-spacing: 0.12px;
            }
            input {
                padding: 12px 16px 16px 16px;
                border-radius: 12px;
                border: 1px solid #8b8b8b;
                background: #262626;
                overflow: hidden;
                color: #dedede;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-family: Instrument Sans;
                font-size: 16px;
                font-style: normal;
                font-weight: 550;
                line-height: 125%;
                align-self: stretch;
                width: 100%;
            }
            input::placeholder {
                overflow: hidden;
                color: #646464;
                text-overflow: ellipsis;
                @include base-font-16;
                line-height: 125%;
            }
            .name-input-wrapper {
                width: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: flex-start;
                gap: 16px;
            }

            .first-name-input-wrapper,
            .last-name-input-wrapper,
            .password-input-wrapper {
                display: flex;
                flex-direction: column;
                justify-content: baseline;
                align-items: flex-start;
                width: 100%;
            }
            .password-input-with-icon {
                position: relative;
                width: 100%;

                .fa {
                    color: #acacac;
                    position: absolute;
                    top: 34%;
                    right: 10px;
                }
            }
            .terms-msg-wrapper {
                color: #646464;
                .terms-msg,
                a {
                    color: #646464;
                    font-family: Instrument Sans;
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 650;
                    line-height: 135%; /* 16.2px */
                    letter-spacing: 0.12px;
                }
            }
        }
        .create-acct-btn {
            padding: 11px 30px 12px 30px;
            border-radius: 100px;
            background: #cdeb09;
            align-self: stretch;
            color: #121212;
            font-family: Instrument Sans;
            font-size: 18px;
            font-style: normal;
            font-weight: 550;
            line-height: 120%;
        }
    }
</style>
